import { useMemo } from 'react';
import { useAppSelector } from '@/store';

/**
 * Checks if Entrata integration is disabled for the current property/org.
 * Returns true when Entrata integration is disabled (maintenance mode).
 * 
 * This hook uses a dual-check approach:
 * 1. Checks the standard 'entrata-integration-disabled' feature flag
 * 2. Falls back to user.entrataIsActive for backward compatibility
 * 
 * @returns boolean - true if Entrata is disabled, false otherwise
 */
export const useEntrataIntegrationDisabled = (): boolean => {
  const user = useAppSelector((s) => s.auth.user as any);

  return useMemo(() => {
    try {
      // Check standard feature flag key (matches FEATURE_FLAG_CONFIG)
      const flags = user?.property?.featureFlags?.flags || {};
      if (typeof flags['entrata-integration-disabled'] === 'boolean') {
        return !!flags['entrata-integration-disabled'];
      }

      // Legacy fallback keys for backward compatibility
      if (typeof flags.entrataDisabled === 'boolean') return !!flags.entrataDisabled;
      if (typeof flags.entrata_integration_disabled === 'boolean') return !!flags.entrata_integration_disabled;

      // Fallback: if user indicates Entrata is inactive (opposite), treat as disabled
      if (typeof user?.entrataIsActive === 'boolean') return user.entrataIsActive === false;

      // Default: not disabled
      return false;
    } catch (error) {
      // If feature flag lookup fails, default to not disabled
      return false;
    }
  }, [user]);
};


